import { FormField, Input, Select, useToast, WeightInput } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  useArchiveArticle,
  useArchiveContainer,
  useArchiveVariety,
  useArticles,
  useContainers,
  useCreateArticle,
  useRenameArticle,
  useSaveContainer,
  useSaveVariety,
  useVarieties,
  type Article,
  type Container,
  type HoneyVariety,
} from '../api';
import { formatKg } from '../format';
import { CatalogRow, CatalogSection, useHideToggle } from './CatalogSection';
import styles from './harvest.module.css';

/** Varieties, containers and articles in Einstellungen → Kataloge (BIZ-003). */
export function HarvestCatalogs() {
  return (
    <>
      <VarietiesCard />
      <ContainersCard />
      <ArticlesCard />
    </>
  );
}

function VarietiesCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState<HoneyVariety | 'new' | null>(null);
  const varieties = useVarieties(hidden);
  const toggle = useHideToggle(useArchiveVariety());
  const write = can('harvest.write');
  return (
    <CatalogSection
      title={t('harvest.catalogs.varieties')}
      hidden={hidden}
      onHidden={setHidden}
      newLabel={t('harvest.catalogs.newVariety')}
      onNew={write ? () => setEditing('new') : undefined}
      loading={varieties.isPending}
    >
      {(varieties.data ?? []).map((v) => (
        <CatalogRow
          key={v.id}
          name={v.name}
          builtin={v.key != null}
          archived={v.archived}
          onEdit={write ? () => setEditing(v) : undefined}
          onToggle={write ? () => toggle(v) : undefined}
        />
      ))}
      {editing && (
        <VarietyDialog
          variety={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </CatalogSection>
  );
}

function VarietyDialog({
  variety,
  onClose,
}: {
  variety?: HoneyVariety | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveVariety();
  const [name, setName] = useState(variety?.name ?? '');
  const errors = formErrors(save.error, { name: ['variety_name', 'variety.name'] });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={variety ? variety.name : t('harvest.catalogs.newVariety')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          { id: variety?.id, name, version: variety?.version },
          {
            onSuccess: () => {
              toast.show(t('apiary.catalogs.saved'));
              onClose();
            },
          },
        )
      }
    >
      <FormField label={t('apiary.catalogs.name')} required error={errors.field.name}>
        {(p) => (
          <Input {...p} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}

function ContainersCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState<Container | 'new' | null>(null);
  const containers = useContainers(hidden);
  const toggle = useHideToggle(useArchiveContainer());
  const write = can('stock.write');
  return (
    <CatalogSection
      title={t('harvest.catalogs.containers')}
      hidden={hidden}
      onHidden={setHidden}
      newLabel={t('harvest.catalogs.newContainer')}
      onNew={write ? () => setEditing('new') : undefined}
      loading={containers.isPending}
    >
      {(containers.data ?? []).map((c) => (
        <CatalogRow
          key={c.id}
          name={c.name}
          facts={[formatKg(c.net_weight_grams, 3)]}
          builtin={c.key != null}
          archived={c.archived}
          onEdit={write ? () => setEditing(c) : undefined}
          onToggle={write ? () => toggle(c) : undefined}
        />
      ))}
      {editing && (
        <ContainerDialog
          container={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </CatalogSection>
  );
}

function ContainerDialog({
  container,
  onClose,
}: {
  container?: Container | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveContainer();
  const [name, setName] = useState(container?.name ?? '');
  const [net, setNet] = useState<number | null>(container?.net_weight_grams ?? null);
  const errors = formErrors(save.error, {
    name: ['container_name', 'container.name'],
    net: ['container.net_weight', 'container.in_use'],
  });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={container ? container.name : t('harvest.catalogs.newContainer')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={errors.general}
      onSubmit={() =>
        save.mutate(
          {
            id: container?.id,
            body: { name, net_weight_grams: net ?? 0 },
            version: container?.version,
          },
          {
            onSuccess: () => {
              toast.show(t('apiary.catalogs.saved'));
              onClose();
            },
          },
        )
      }
    >
      <div className={styles.row}>
        <FormField label={t('apiary.catalogs.name')} required error={errors.field.name}>
          {(p) => (
            <Input {...p} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
          )}
        </FormField>
        <FormField
          label={t('harvest.catalogs.net')}
          required
          error={errors.field.net}
          hint={container?.used ? t('harvest.catalogs.netLocked') : undefined}
        >
          {(p) => (
            <WeightInput
              {...p}
              value={net}
              onChange={setNet}
              stepKg={0.05}
              disabled={container?.used ?? false}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
      </div>
    </FormDialog>
  );
}

function ArticlesCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState<Article | 'new' | null>(null);
  const articles = useArticles(hidden);
  const toggle = useHideToggle(useArchiveArticle());
  const write = can('stock.write');
  return (
    <CatalogSection
      title={t('harvest.catalogs.articles')}
      hidden={hidden}
      onHidden={setHidden}
      newLabel={t('harvest.catalogs.newArticle')}
      onNew={write ? () => setEditing('new') : undefined}
      loading={articles.isPending}
      empty={articles.data?.length === 0 ? t('harvest.catalogs.noArticles') : undefined}
    >
      {(articles.data ?? []).map((a) => (
        <CatalogRow
          key={a.id}
          name={a.name}
          facts={[a.variety.name, a.container.name, t('harvest.articles.count', { count: a.jars })]}
          archived={a.archived}
          onEdit={write ? () => setEditing(a) : undefined}
          onToggle={write ? () => toggle(a) : undefined}
        />
      ))}
      {editing && (
        <ArticleDialog
          article={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </CatalogSection>
  );
}

function ArticleDialog({
  article,
  onClose,
}: {
  article?: Article | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const varieties = useVarieties();
  const containers = useContainers();
  const create = useCreateArticle();
  const rename = useRenameArticle();
  const mutation = article ? rename : create;
  const [name, setName] = useState(article?.name ?? '');
  const [variety, setVariety] = useState('');
  const [container, setContainer] = useState('');
  const varietyValue = variety || varieties.data?.[0]?.id || '';
  const containerValue = container || containers.data?.[0]?.id || '';
  // Without a name the server names the article "variety container".
  const defaultName = [
    varieties.data?.find((v) => v.id === varietyValue)?.name,
    containers.data?.find((c) => c.id === containerValue)?.name,
  ]
    .filter(Boolean)
    .join(' ');
  const errors = formErrors(mutation.error, { name: ['article_name'] });
  const done = () => {
    toast.show(t('apiary.catalogs.saved'));
    onClose();
  };
  return (
    <FormDialog
      open
      onClose={onClose}
      title={article ? article.name : t('harvest.catalogs.newArticle')}
      submitLabel={t('common.save')}
      pending={mutation.isPending}
      error={errors.general}
      onSubmit={() =>
        article
          ? rename.mutate({ id: article.id, name, version: article.version }, { onSuccess: done })
          : create.mutate(
              { variety_id: varietyValue, container_id: containerValue, name: name || null },
              { onSuccess: done },
            )
      }
    >
      {!article && (
        <div className={styles.row}>
          <FormField label={t('harvest.batch.variety')} required>
            {(p) => (
              <Select
                {...p}
                value={varietyValue}
                onChange={(e) => setVariety(e.target.value)}
                options={(varieties.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              />
            )}
          </FormField>
          <FormField label={t('harvest.catalogs.container')} required>
            {(p) => (
              <Select
                {...p}
                value={containerValue}
                onChange={(e) => setContainer(e.target.value)}
                options={(containers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
          </FormField>
        </div>
      )}
      <FormField label={t('apiary.catalogs.name')} required={!!article} error={errors.field.name}>
        {(p) => (
          <Input
            {...p}
            value={name}
            maxLength={80}
            {...(article ? {} : { placeholder: defaultName })}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
